import { useEffect, useState } from "react";
import { UserPlus, Trash2, Mail, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Founder-only panel to manage the invite-only tester list for the private
 * deployment phase. Only emails in this list can register at /register.
 *
 * When INVITE_ONLY_MODE=false in backend/.env, the gate is bypassed and this
 * panel becomes informational only (an amber "public sign-ups enabled" banner
 * appears at the top).
 */
export const InvitedTesters = () => {
  const [state, setState] = useState({ loading: true, invite_only: true, invites: [] });
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [adding, setAdding] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/invites");
      setState({ loading: false, invite_only: data.invite_only, invites: data.invites || [] });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error("Could not load invites");
    }
  };

  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!newEmail.trim() || adding) return;
    setAdding(true);
    try {
      const { data } = await api.post("/invites", { email: newEmail.trim(), note: newNote.trim() || null });
      if (data.already_invited) {
        toast.message(`${newEmail.trim()} was already on the list`);
      } else if (data.already_registered) {
        toast.success(`Added ${newEmail.trim()} — they've already registered ✓`);
      } else {
        toast.success(`Invited ${newEmail.trim()}. Share the URL with them so they can register.`);
      }
      setNewEmail("");
      setNewNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add invite");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the invited list?\n\nIf they've already registered, they'll keep their account — this only removes future access for that email if they haven't signed up yet.`)) return;
    try {
      await api.delete(`/invites/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove invite");
    }
  };

  if (state.loading) {
    return (
      <section data-testid="invited-testers-panel" style={{ padding: 24, marginBottom: 32, border: "1px solid var(--border-default)" }}>
        <Loader2 size={16} className="spin" /> Loading invites…
      </section>
    );
  }

  return (
    <section
      data-testid="invited-testers-panel"
      style={{
        border: "1px solid var(--border-default)",
        background: "var(--surface)",
        padding: 24,
        marginBottom: 32,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <UserPlus size={16} strokeWidth={1.5} color="var(--brand-accent)" />
            <span style={{
              fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
              fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: "var(--brand-accent)",
            }}>
              Private deployment
            </span>
          </div>
          <h2 className="font-serif" style={{ fontSize: 22, marginBottom: 6 }}>Invited testers</h2>
          <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-secondary)" }}>
            {state.invite_only
              ? "Only emails on this list can register for Le Salon. Add trusted testers by email while you QA. When you're ready to launch publicly, flip INVITE_ONLY_MODE=false in the backend env — this list becomes informational only."
              : "Public sign-ups are currently enabled. This list is informational — anyone can register."}
          </p>
        </div>
        <div
          data-testid="invite-mode-badge"
          style={{
            padding: "6px 14px",
            border: `1px solid ${state.invite_only ? "var(--brand-accent)" : "#B37D2C"}`,
            color: state.invite_only ? "var(--brand-accent)" : "#B37D2C",
            fontFamily: "'Outfit', sans-serif",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 700,
            alignSelf: "center",
          }}
        >
          {state.invite_only ? "Private" : "Public sign-ups on"}
        </div>
      </div>

      {/* Add form */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="email"
          data-testid="invite-email-input"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="tester@email.com"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          style={{
            flex: "1 1 220px", minWidth: 200,
            padding: "10px 14px", fontSize: 14,
            border: "1px solid var(--border-default)",
            background: "var(--bg-alt)", color: "var(--text-primary)",
            fontFamily: "'Outfit', sans-serif",
          }}
        />
        <input
          type="text"
          data-testid="invite-note-input"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Optional note (e.g. Tina — QA tester)"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          style={{
            flex: "1 1 220px", minWidth: 200,
            padding: "10px 14px", fontSize: 14,
            border: "1px solid var(--border-default)",
            background: "var(--bg-alt)", color: "var(--text-primary)",
            fontFamily: "'Outfit', sans-serif",
          }}
        />
        <button
          type="button"
          data-testid="invite-add-button"
          onClick={add}
          disabled={!newEmail.trim() || adding}
          className="btn-accent"
          style={{
            padding: "10px 20px", fontSize: 12, letterSpacing: "0.14em",
            textTransform: "uppercase", fontWeight: 600,
            opacity: !newEmail.trim() || adding ? 0.55 : 1,
            cursor: !newEmail.trim() || adding ? "not-allowed" : "pointer",
          }}
        >
          {adding ? "Adding…" : "Invite"}
        </button>
      </div>

      {/* List */}
      {state.invites.length === 0 ? (
        <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          No testers invited yet.
        </div>
      ) : (
        <ul data-testid="invite-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {state.invites.map((inv) => (
            <li
              key={inv.email}
              data-testid={`invite-row-${inv.email}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "10px 14px",
                border: "1px solid var(--border-default)",
                background: "var(--bg-alt)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
                {inv.claimed_at ? (
                  <CheckCircle2 size={16} strokeWidth={1.8} color="#2F7A3F" />
                ) : (
                  <Clock size={16} strokeWidth={1.6} color="var(--text-muted)" />
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Mail size={12} strokeWidth={1.6} color="var(--text-muted)" />
                    <span style={{ fontSize: 14, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {inv.email}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    {inv.claimed_at ? "Registered" : "Pending — not yet signed up"}
                    {inv.note && ` · ${inv.note}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid={`invite-remove-${inv.email}`}
                onClick={() => remove(inv.email)}
                aria-label={`Remove ${inv.email}`}
                title="Remove from invited list"
                style={{
                  background: "transparent", border: "none",
                  color: "var(--text-muted)", cursor: "pointer",
                  padding: 6, flexShrink: 0,
                }}
              >
                <Trash2 size={14} strokeWidth={1.6} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default InvitedTesters;
